import { watch } from "vue"

/**
 * Sets contact details against a given form
 * 
 * @param {Object} form 
 * @param {Object} contact 
 * @param {boolean} [setConfirmation=false]
 */
export function setContact(form, contact, setConfirmation = false) {
    form.fields.value.contact.email                    = contact.email
    form.fields.value.contact.contact_number           = contact.contact_number
    form.fields.value.contact.mobile_number            = contact.mobile_number
    form.fields.value.contact.address_line_1           = contact.address_line_1
    form.fields.value.contact.address_line_2           = contact.address_line_2
    form.fields.value.contact.city                     = contact.city
    form.fields.value.contact.postcode                 = contact.postcode
    form.fields.value.contact.county                   = contact.county
    form.fields.value.contact.country                  = contact.country

    if(setConfirmation){
        form.fields.value.contact.email_confirmation          = contact.email
        form.fields.value.contact.contact_number_confirmation = contact.contact_number
    }
}

/**
 * Creates a watch for the membership type details to set the contact details if required
 * 
 * @param {Object} data 
 * @param {Object} form 
 */
export function createMembershipTypeDetailsWatch(data, form){
    return watch(
        data.membership_type.details,
        (membershipTypeDetails) => {
            if(data?.user?.is_authenticated?.value){
    
                const memberType = membershipTypeDetails?.entity_type?.value
    
                if(memberType === 'organisation' && data.member.has_organisation_contact.value){
                    setContact(form, data.member.details.value.organisation_member?.contact)
                } else if(memberType === 'individual' && data.member.has_individual_contact.value){
                    setContact(form, data.member.details.value.individual_member?.contact)
                }
            }

            setDefaults(membershipTypeDetails?.fields ?? [], form)
        }
    )
}

/**
 * Sets the default values against the form from a given set of fields
 * 
 * @param {Array} fields 
 * @param {Object} form 
 */
export const setDefaults = function(fields, form){
    fields.forEach(
        (field) => {
            if(form.fields.value?.fields[field?.id] === undefined){
                const defaults = field.options.reduce((options, option) => {
                    if(option.default){
                        options.push(option?.id)
                    }

                    return options
                }, [])

                if(defaults.length > 0){
                    form.fields.value.fields[field?.id] = field.multiple
                        ? defaults
                        : defaults[0]
                }
            }
        } 
    )
}

/**
 * Gets the valid answers from a list of answers for a given set of fields
 * 
 * @param {Object} answers
 * @param {Object} validFields
 * @param {Object} existingAnswers
 * @param {Boolean} allowRenewals
 * 
 * @returns {Object}
 */
export const getValidFields = function(answers, validFields, existingAnswers = false, allowRenewals = false){
    return Object.values(validFields)
        .reduce((finalFields, field) => {
            //Remove fields which cannot be set by the user
            if(['viewable_by_user', 'admin_only'].includes(field.field_visibility.value)){
                return finalFields
            }

            //Remove renewal fields when we aren't handling renewals
            if(!allowRenewals && field.field_visibility.value === 'renewal_only'){
                return finalFields
            }

            //Remove fields which cannot be modified by the user
            if(
                typeof existingAnswers === 'object' 
                && existingAnswers[field.id] !== undefined
                && field.field_visibility.value === 'assignable_once_by_user'
            ){
                return finalFields
            }

            if(answers[field.id] !== undefined){ //null should be accepted
                finalFields[field.id] = answers[field.id]
            }

            if(field.confirmed && answers[`${field.id}_confirmation`]){
                finalFields[`${field.id}_confirmation`] = answers[`${field.id}_confirmation`]
            }

            return finalFields
        }, {})
}